import { ChevronDown } from 'lucide-react';
import type { RefObject } from 'react';

import { cn } from '@rocco/utils/cn';

/**
 * The invitation is gone by this much of the turn's growth.
 *
 * Set so it finishes just before the section's top edge reaches the foot of
 * the invitation: what covers it is then an empty patch of ground rather than
 * a hard edge wiping across live text.
 */
const FADE_BY = 0.35;

/** The invitation's opacity at a given progress of the turn's growth. */
export const nextInvitationOpacity = (progress: number) =>
    Math.max(0, 1 - progress / FADE_BY);

/**
 * Tighter than the page titles' -0.03em: at 160px the same tracking leaves
 * the letters adrift, and this is set to read as one mass rather than as
 * words.
 */
const INVITATION_TYPE = cn(
    'm-0 font-sans font-medium tracking-[-0.055em]',
    'max-md:text-[4.5rem] max-md:leading-[1.05]',
    'md:text-[10rem] md:leading-[8.5rem]',
);

const INK = {
    light: { invitation: 'text-[#323232]', prompt: 'text-[#b1b1b1]' },
    dark: { invitation: 'text-[#e5e5e5]', prompt: 'text-[#7a7a7a]' },
} as const;

/**
 * "Next Product", "Next Project", "Next Article": the held, centred invitation
 * that stands before a detail page's turn.
 *
 * It sits above the turn's section rather than inside it, so it adds scroll
 * to travel through and changes nothing about the growth, which measures from
 * the section's own top edge.
 *
 * It holds at the middle of the screen for the whole of the 800px under it,
 * so the reader passes it on the way in and cannot mistake what is coming.
 * The extra viewport inside the box, taken straight back off as margin, is
 * what keeps it held while the section arrives: the section still begins
 * 800px under it, but the box the sticky child is held in runs on past that,
 * so the section rides up over the invitation rather than shunting it off the
 * top. The section that follows has to be positioned above it (`relative
 * z-10`) and opaque, or the sticky child — itself positioned — paints over it.
 *
 * The caller fades it through `fadeRef` with `nextInvitationOpacity`, on the
 * turn's own progress.
 */
export const NextListingInvitation = ({
    label,
    fadeRef,
    tone = 'light',
}: {
    label: string;
    /** The block the turn fades — the line and the prompt under it, whole. */
    fadeRef: RefObject<HTMLDivElement | null>;
    /** The ground it stands on. The project page is dark. */
    tone?: 'light' | 'dark';
}) => (
    <div className="relative z-0 -mb-[100vh] w-full pb-[50rem] max-md:px-6 md:px-[3rem]">
        <div
            ref={fadeRef}
            className={cn(
                'sticky top-1/2 flex -translate-y-1/2 flex-col items-center gap-[1rem] text-center',
                // Held lit where the turn does not animate. The growth hook
                // draws a reduced-motion reader straight to a finished turn,
                // and a finished turn's fade is zero — which left the
                // invitation invisible above the scroll it still holds for.
                // It has to beat the inline opacity the fade writes.
                'motion-reduce:opacity-100!',
            )}
        >
            <p className={cn(INVITATION_TYPE, INK[tone].invitation)}>{label}</p>
            {/* Dropped clear of the invitation by a transform rather than by a
                gap: the block keeps the height it is centred on, so the line
                above stays exactly where it sits and nothing below is pushed
                down. */}
            <span
                className={cn(
                    'flex translate-y-[3.75rem] flex-col items-center gap-[0.375rem]',
                    // The FAQ's reading type, so the one small line here reads
                    // at the size the page's prose does.
                    'font-sans max-md:text-base md:text-[1.25rem]',
                    INK[tone].prompt,
                )}
            >
                Keep scrolling
                <ChevronDown
                    aria-hidden="true"
                    className="size-[1.25rem]"
                    strokeWidth={1.5}
                />
            </span>
        </div>
        {/* The viewport the invitation is held for while the section covers
            it: a spacer inside the box, given straight back by the box's
            negative margin, so the document is no taller for it. */}
        <div aria-hidden="true" className="h-screen" />
    </div>
);
